import {Text} from '@mantine/core';
import Image from 'next/image';
import {useRouter} from 'next/router';
import type {FC} from 'react';
import {MdAccountCircle, MdClear, MdMenu, MdOpenInNew} from 'react-icons/md';
import LOGO_DEFAULT from '../../../../../public/next-image/common/engineerLogo/default.svg';
import {COLOR_BLACK_1} from '../../../../config/constants/colorCodes';
import {GSSKT_HOST} from '../../../../config/constants/hosts';
import {ENGINEER_FRONT_BANNER} from '../../../../config/constants/img';
import {
  ENGINEER,
  ENGINEER_AI_ES_BUILDER,
  ENGINEER_COLUMN,
  ENGINEER_COMPANY,
  ENGINEER_DRILL,
  ENGINEER_RECRUITING_INFO,
  ENGINEER_SKILL_CHECK_TOP,
  LOGIN,
  REPORT_ANALYTICS_SEARCH,
} from '../../../../config/constants/routes';
import {isReleasedNewRegisterFlow} from '../../../../config/unReleasePage';
import {QUERY_PARAMS_FOR_REDIRECT_FROM_LOGIN_PAGE} from '../../../../core/entities/login/constants';
import {useAuth} from '../../../../core/usecases/auth/AuthContext';
import {useBooleanState} from '../../../services/useBooleanState';
import {CommonLink} from '../../CommonLink';
import {HeaderMenu} from '../Menu';
import {isStudentVerificationRequiredMenu} from './studentVerificationRequiredMenu';

type Props = {
  scoutsCount: number;
  interestsCount: number;
  className?: string;
};

const menuItems: Array<{
  name: string;
  url: string;
  isNew?: boolean;
  isUpdate?: boolean;
}> = [
  {
    name: '募集を探す',
    url: ENGINEER_RECRUITING_INFO,
  },
  {
    name: '企業を探す',
    url: ENGINEER_COMPANY,
  },
  {
    name: 'ES・体験記',
    url: '/engineer/company/reports',
  },
  {
    name: 'コラム',
    url: ENGINEER_COLUMN,
  },
  {
    name: 'イベント',
    url: `${ENGINEER}#events`,
  },
  {
    name: 'AI選考対策',
    url: REPORT_ANALYTICS_SEARCH,
  },
  {
    name: 'スキルチェック',
    url: ENGINEER_SKILL_CHECK_TOP,
  },
  {
    name: '開発ドリル',
    url: '/engineer/drill',
    isNew: true,
  },
  {
    name: 'AI ESビルダー',
    url: ENGINEER_AI_ES_BUILDER,
    isUpdate: true,
  },
];

const topPageMenuItems: Array<{
  name: string;
  url: string;
  isNew?: boolean;
  isUpdate?: boolean;
}> = [
  {
    name: '募集を探す',
    url: '#recruiting_info',
  },
  {
    name: '企業を探す',
    url: '#companies',
  },
  {
    name: 'ES・体験記',
    url: '#company_reports',
  },
  {
    name: 'コラム',
    url: '#columns',
  },
  {
    name: 'イベント',
    url: '#events',
  },
  {
    name: 'AI選考対策',
    url: REPORT_ANALYTICS_SEARCH,
  },
  {
    name: 'スキルチェック',
    url: ENGINEER_SKILL_CHECK_TOP,
  },
  {
    name: '開発ドリル',
    url: ENGINEER_DRILL,
    isNew: true,
  },
  {
    name: 'AI ESビルダー',
    url: ENGINEER_AI_ES_BUILDER,
    isUpdate: true,
  },
];

const hamburgerList: Array<{
  name: string;
  url: string;
}> = [
  {
    name: '外資就活トップ',
    url: '/',
  },
  {
    name: 'コミュニティ',
    url: '/questions',
  },
  {
    name: '相談室',
    url: '/box/answers',
  },
  {
    name: '各種設定・その他',
    url: '/mypage/profile',
  },
];

export const EngineerPcHeader: FC<Props> = ({
  className,
  scoutsCount,
  interestsCount,
}) => {
  const {status} = useAuth();

  // フロントバナーの表示期間を設定
  const FRONT_BANNER_START_DATE = '2026-10-02T00:00:00+09:00';
  const FRONT_BANNER_END_DATE = '2026-10-15T23:59:59+09:00';

  const currentDate = new Date();

  const isBannerVisible =
    currentDate >= new Date(FRONT_BANNER_START_DATE) &&
    currentDate <= new Date(FRONT_BANNER_END_DATE);

  const BannerURL = `${GSSKT_HOST}/recruiting_info/view/15174`;

  return (
    <div className={className}>
      {status === 'authenticated' ? (
        <AuthedHeader
          scoutsCount={scoutsCount}
          interestsCount={interestsCount}
        />
      ) : (
        <GuestHeader />
      )}
      {isBannerVisible && (
        <div className={`w-full ${status !== 'authenticated' && 'mt-[64px]'}`}>
          <CommonLink href={BannerURL} target="_blank">
            <Image
              src={ENGINEER_FRONT_BANNER}
              alt="DeNA AIジェネラリストコース エンジニア職 本選考応募受付中"
              width={1280}
              height={96}
              className="w-full h-auto object-cover"
            />
          </CommonLink>
        </div>
      )}
    </div>
  );
};

const AuthedHeader: FC<{
  scoutsCount: number;
  interestsCount: number;
}> = ({scoutsCount, interestsCount}) => {
  const {user, signOut} = useAuth();
  const [shownMenu, showMenu, hideMenu] = useBooleanState(false);
  const [hamburgerOpened, , _hamburgerClose, hamburgerToggle] =
    useBooleanState(false);
  const hasNotification = scoutsCount > 0 || interestsCount > 0;
  const router = useRouter();
  const baseMenu = router.pathname === ENGINEER ? topPageMenuItems : menuItems; // TOPページはアンカーリンク、それ以外は一覧ページに遷移
  // 大学認証が完了していないユーザーには一部メニューを表示しない
  const menu =
    isReleasedNewRegisterFlow() && user?.isStudentVerified === false
      ? baseMenu.filter((item) => !isStudentVerificationRequiredMenu(item.url))
      : baseMenu;

  return (
    <div>
      <div className="flex h-64 items-center justify-between px-48 text-base fixed w-full top-0 z-50 bg-white">
        <div className="flex items-center gap-20">
          <button
            type="button"
            id="drawer-button"
            aria-label="Drawer Button"
            onClick={hamburgerToggle}
            className="pc:hover:opacity-80 pc:hover:duration-300 pc:hover:ease-in-out "
          >
            {hamburgerOpened ? (
              <MdClear size={24} fill={COLOR_BLACK_1} />
            ) : (
              <div className="relative">
                <MdMenu fill={COLOR_BLACK_1} size={24} />
              </div>
            )}
          </button>
          <CommonLink href={ENGINEER}>
            <Image src={LOGO_DEFAULT} alt="Software Engineer 就活" />
          </CommonLink>
        </div>
        <div className="flex items-center gap-16">
          <div className="flex gap-16">
            {menu.map((item) => (
              <CommonLink
                key={item.name}
                href={item.url}
                className="font-bold text-black-1 flex items-center"
              >
                <div
                  className={`bg-accent-1 text-white font-bold rounded-12 text-center text-[8px] h-12 mr-4 ${item.isNew ? 'w-32' : 'w-40'} ${item.isNew || item.isUpdate ? '' : 'hidden'}`}
                >
                  {item.isUpdate && 'UPDATE'}
                  {item.isNew && 'NEW'}
                </div>
                {item.name}
              </CommonLink>
            ))}
          </div>
          <button
            type="button"
            className="relative flex items-center gap-8 text-black-1"
            onClick={() => {
              if (shownMenu) {
                hideMenu();
              } else {
                showMenu();
              }
            }}
            onMouseEnter={() => {
              showMenu();
            }}
            onMouseLeave={() => {
              hideMenu();
            }}
            aria-label={shownMenu ? 'メニューを閉じる' : 'メニューを開く'}
          >
            <div className="relative">
              <MdAccountCircle size={24} />
              {hasNotification && (
                <div className="absolute right-0 top-0 flex h-8 w-8 rounded-full bg-notification" />
              )}
            </div>
            <div className="text-sm font-bold">{user?.familyName}さん</div>
            {shownMenu && (
              <div className="absolute right-0 top-[22px] z-50 pt-[26px] ">
                <div className="overflow-hidden rounded-12 bg-background-1 shadow-modal">
                  <HeaderMenu
                    onItemClick={hideMenu}
                    scoutsCount={scoutsCount}
                    interestsCount={interestsCount}
                    className="w-[184px] text-black-1"
                    onLogout={signOut}
                    showMyPage={true}
                  />
                </div>
              </div>
            )}
          </button>
        </div>
        {hamburgerOpened && (
          <div className="absolute top-48 left-48 bg-white shadow-modal p-12 flex flex-col z-10 rounded-12">
            {hamburgerList.map((item) => (
              <button
                type="button"
                key={item.url}
                onClick={() => {
                  window.open(item.url);
                }}
                className="w-[160px] p-8 border-b-[1px] border-line text-base text-left justify-between flex items-center"
              >
                <Text>{item.name}</Text>
                <MdOpenInNew />
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="w-full border-[0.5px]" />
      <div className="w-full py-12 pl-48 h-[45px] flex text-black-1 font-bold text-base mt-[50px]">
        エンジニアとして理想のファーストキャリアを目指そう
      </div>
    </div>
  );
};

const GuestHeader: FC = () => {
  const router = useRouter();

  return (
    <div className="flex h-64 items-center justify-between bg-white px-64 text-base shadow-modal fixed w-full top-0 z-50">
      <div className="flex items-center gap-20">
        <CommonLink href={ENGINEER}>
          <Image src={LOGO_DEFAULT} alt="Software Engineer 就活" />
        </CommonLink>
      </div>
      <div className="flex h-64 items-center justify-between bg-white">
        <div className="flex items-center gap-16">
          <CommonLink
            href={`${LOGIN}?${QUERY_PARAMS_FOR_REDIRECT_FROM_LOGIN_PAGE}=${router.asPath}`}
            className="font-bold text-primary-1"
          >
            ログイン・新規登録
          </CommonLink>
        </div>
      </div>
    </div>
  );
};
